import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { useCustomerConfig } from '../hooks/useCustomerConfig';
import { BottomNavigation } from '../components/BottomNavigation';
import { AppFooter } from '../components/AppFooter';

const FLOWER_CARE_ITEMS = [
  'Обновите срез стеблей под углом 45°',
  'Используйте чистую вазу и свежую воду',
  'Меняйте воду каждые 2–3 дня',
  'Держите букет вдали от прямых солнечных лучей',
  'Удаляйте увядшие цветы для продления жизни букета',
];

const CONDITIONS_RETURN_TEXT = `Цветы относятся к категории живых товаров и не подлежат обмену или возврату надлежащего качества (согласно Перечню непродовольственных товаров, утв. Постановлением Правительства РФ №55).

Когда можно отказаться?
Вы вправе отказаться от заказа в момент доставки, если товар оказался ненадлежащего качества (испорчен, имеет дефекты). Это регулируется п. 3 ст. 497 ГК РФ и ст. 21 Закона «О защите прав потребителей».

Возврат денежных средств
При оплате банковской картой возврат возможен только на ту же карту, с которой была совершена операция. Оформить возврат наличными при электронной оплате нельзя.

Сроки зачисления
От 3 до 40 рабочих дней в зависимости от политики вашего банка.`;

function CollapsibleBlock({
  title,
  children,
  defaultExpanded = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <div
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '12px',
        marginBottom: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(200, 63, 127, 0.08)',
        border: '1px solid var(--border-light)',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '14px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontWeight: 600,
          fontSize: '15px',
          color: 'var(--text-primary)',
        }}
      >
        <span style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(90deg)' : 'rotate(0)', color: 'var(--color-accent)' }}>
          ▶
        </span>
        {title}
      </button>
      {expanded && (
        <>
          <div style={{ height: '2px', backgroundColor: 'var(--color-accent)', margin: '0 16px' }} />
          <div style={{ padding: '14px 16px 18px', fontSize: '14px', lineHeight: 1.6, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}

export default function AboutPage() {
  const navigate = useNavigate();
  const { config } = useCustomerConfig();

  useEffect(() => {
    WebApp.MainButton.hide();
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-main)',
      paddingBottom: '120px',
    }}>
      {/* Заголовок с кнопкой назад */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        padding: '12px 16px',
        color: 'var(--text-primary)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '2px solid var(--color-accent)',
      }}>
        <button
          onClick={(e) => {
            e.preventDefault();
            navigate('/catalog', { replace: false });
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div style={{ fontSize: '16px', fontWeight: 'bold', flex: 1 }}>О нас</div>
      </div>

      <div className="container" style={{ paddingTop: '20px' }}>
        {/* Заголовок бренда */}
        <h2 style={{ margin: '0 0 16px', fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {config?.brand?.displayName || 'Бери Охапку'}
        </h2>

        {/* Блоки с иконками (преимущества) */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              boxShadow: '0 2px 12px rgba(200, 63, 127, 0.1)',
              border: '1px solid var(--border-light)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-accent)',
                margin: '0 auto 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="1.5">
              <circle cx="12" cy="12" r="2.5" fill="white" />
              <circle cx="12" cy="6" r="2" fill="white" />
              <circle cx="17" cy="9" r="2" fill="white" />
              <circle cx="17" cy="15" r="2" fill="white" />
              <circle cx="12" cy="18" r="2" fill="white" />
              <circle cx="7" cy="15" r="2" fill="white" />
              <circle cx="7" cy="9" r="2" fill="white" />
            </svg>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>Свежие и качественные цветы</div>
          </div>
          <div
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center',
              boxShadow: '0 2px 12px rgba(200, 63, 127, 0.1)',
              border: '1px solid var(--border-light)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: 'var(--color-accent)',
                margin: '0 auto 10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="3" width="15" height="13" />
                <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                <circle cx="5.5" cy="18.5" r="2.5" />
                <circle cx="18.5" cy="18.5" r="2.5" />
              </svg>
            </div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>Быстрая доставка</div>
          </div>
        </div>

        {/* О проекте */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>О проекте</h3>
          <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            {config?.brand?.displayName || 'Бери Охапку'} — инновационный проект в сфере розничной продажи цветов. Наша цель — сделать покупку цветов удобной и доступной.
          </p>
        </div>

        {/* Наша миссия */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>Наша миссия</h3>
          <p style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--text-secondary)' }}>
            Создать новый стандарт продажи цветов: каждый клиент получает свежие цветы по себестоимости с быстрой доставкой в удобное время.
          </p>
        </div>

        {/* Что включает сервисный сбор */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-primary)' }}>Что включает в себя сервисный сбор 10%?</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Сервисный сбор 10% от стоимости заказа. Доставка оплачивается отдельно.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { icon: '📋', text: 'Сборка на складе' },
              { icon: '+', text: 'Транспортировочная упаковка (коробка)' },
              { icon: '🏭', text: 'Налог' },
            ].map((item) => (
              <div
                key={item.text}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 14px',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '10px',
                  border: '1px solid var(--border-light)',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    backgroundColor: 'var(--color-accent)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 600,
                  }}
                >
                  {item.icon}
                </div>
                <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-primary)' }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Преимущества */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>Преимущества</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { icon: '💰', text: 'Прозрачные цены' },
              { icon: '⭐', text: 'Программа лояльности' },
            ].map((item) => (
              <div
                key={item.text}
                style={{
                  padding: '16px',
                  backgroundColor: 'var(--bg-surface)',
                  borderRadius: '12px',
                  textAlign: 'center',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-accent)',
                    color: 'white',
                    margin: '0 auto 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '22px',
                  }}
                >
                  {item.icon}
                </div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)' }}>{item.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Сворачиваемые блоки */}
        <div style={{ marginBottom: '24px' }}>
          <CollapsibleBlock title="Уход за букетом" defaultExpanded>
            {FLOWER_CARE_ITEMS.map((item, i) => (
              <div key={i} style={{ marginBottom: i < FLOWER_CARE_ITEMS.length - 1 ? '8px' : 0 }}>
                {i + 1}. {item}
              </div>
            ))}
          </CollapsibleBlock>
          <CollapsibleBlock title="Доставка и оплата">
            <div style={{ marginBottom: '8px' }}><strong>Стоимость по зонам:</strong></div>
            <ul style={{ margin: '0 0 12px 16px', padding: 0 }}>
              {(config?.delivery?.zones || [
                { name: 'Чебоксары', price: '350 ₽' },
                { name: 'Новочебоксарск', price: '400 ₽' },
              ]).map((z) => (
                <li key={z.name}>{z.name} — {z.price}</li>
              ))}
            </ul>
            <div>Рабочие часы: {config?.delivery?.workingHours || '09:00–20:00'}. {config?.delivery?.avgTime || 'Срочная доставка'}.</div>
            <div style={{ marginTop: '12px' }}><strong>Оплата:</strong> {config?.payments?.methods?.join(', ') || 'по ссылке или QR-коду.'}</div>
          </CollapsibleBlock>
          <CollapsibleBlock title="Условия возврата" defaultExpanded>
            {CONDITIONS_RETURN_TEXT}
          </CollapsibleBlock>
          <CollapsibleBlock title="Политика обработки данных">
            <div>
              Мы обрабатываем персональные данные (идентификатор Telegram, имя, телефон, адрес) для приёма заказов, связи с вами и работы программы лояльности. Данные хранятся на защищённых серверах. Вы вправе запросить доступ, уточнение или удаление данных.
              <br /><br />
              <button
                type="button"
                onClick={() => navigate('/rules')}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: 'var(--color-accent)',
                  fontSize: '14px',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Полный текст политики
              </button>
            </div>
          </CollapsibleBlock>
        </div>

        {/* Контакты */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-primary)' }}>Контакты</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: 'var(--text-primary)', fontSize: '14px' }}>
            <div><strong>Телефон:</strong> <a href={`tel:${config?.contacts?.phone || '+79519982998'}`} style={{ color: 'var(--color-accent)' }}>{config?.contacts?.phone || '+7 951 998-29-98'}</a></div>
            {config?.contacts?.email && <div><strong>Email:</strong> <a href={`mailto:${config?.contacts?.email}`} style={{ color: 'var(--color-accent)' }}>{config?.contacts?.email}</a></div>}
            <div><strong>Адрес:</strong> {config?.contacts?.address || 'г. Чебоксары, ул. Калинина, 86'}</div>
            <div><strong>График:</strong> {config?.contacts?.workHours || 'ежедневно 09:00–20:00'}</div>
            {(config?.contacts?.social?.telegram || config?.contacts?.social?.instagram) && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                {config?.contacts?.social?.telegram && (
                  <a href={config.contacts.social.telegram} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)' }}>Telegram</a>
                )}
                {config?.contacts?.social?.instagram && (
                  <a href={config.contacts.social.instagram} target="_blank" rel="noreferrer" style={{ color: 'var(--color-accent)' }}>Instagram</a>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            type="button"
            onClick={() => navigate('/rules')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-accent)',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            📄 Правила и политика конфиденциальности
          </button>
        </div>
      </div>

      <AppFooter />
      <BottomNavigation />
    </div>
  );
}
